import { useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Zap, Sparkles, Crown, Beaker, Loader2, ArrowRight } from "lucide-react";

const PLANS = [
  { id: "free",    label: "Free trial", desc: "1 project, severely limited",       Icon: Zap },
  { id: "starter", label: "Starter",    desc: "$19/mo · 1 project · Simple Mode",  Icon: Sparkles },
  { id: "pro",     label: "Pro",        desc: "$39/mo · 5 projects · full studio", Icon: Sparkles },
  { id: "agency",  label: "Agency",     desc: "$99/mo · 25 projects · white-label", Icon: Crown },
];

/**
 * Admin-only "QA tool": instantly switch your own account between plans without
 * touching Stripe. Flips user.plan in MongoDB so every gate, feature flag, and
 * UI branch immediately reflects the chosen tier on the next render.
 *
 * Lives on /admin. Useful for testing pricing, paywalls, dashboard variants.
 */
export default function AdminPlanSwitcher() {
  const { user, refresh } = useAuth();
  const [busy, setBusy] = useState("");
  if (!user || user.role !== "admin") return null;

  const current = user.plan || "free";

  const switchTo = async (plan) => {
    if (plan === current) return;
    setBusy(plan);
    try {
      const { data } = await api.post("/admin/me/plan", { plan });
      await refresh();
      toast.success(`Switched to ${data.plan.toUpperCase()}`, {
        description: data.previous_plan ? `Previously: ${data.previous_plan}` : undefined,
      });
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy("");
    }
  };

  return (
    <section className="border border-foreground bg-secondary/30 p-6 md:p-8 mb-10" data-testid="admin-plan-switcher">
      <div className="flex items-start gap-4 mb-5">
        <div className="w-10 h-10 bg-foreground text-background grid place-items-center flex-shrink-0">
          <Beaker className="w-5 h-5" strokeWidth={1.5} />
        </div>
        <div className="min-w-0">
          <div className="font-mono-data text-[10px] uppercase tracking-widest text-muted-foreground">
            // qa tool · admin-only · bypasses stripe
          </div>
          <h2 className="font-display tracking-tight text-xl mt-0.5">Switch your own plan</h2>
          <p className="text-sm text-muted-foreground mt-1">
            Instantly flip this admin account between tiers so you can see what gates trigger, what features
            appear, and how the dashboard renders for each plan. Doesn&apos;t touch Stripe — pure DB flip.
            Logged to <code className="font-mono-data text-[11px]">admin_actions</code>.
          </p>
        </div>
      </div>

      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3" data-testid="admin-plan-switcher-grid">
        {PLANS.map(({ id, label, desc, Icon }) => {
          const active = id === current;
          const loading = busy === id;
          return (
            <button
              key={id}
              type="button"
              onClick={() => switchTo(id)}
              disabled={loading || !!busy}
              className={`text-left border p-4 transition-colors ${
                active
                  ? "border-foreground bg-foreground text-background"
                  : "border-border bg-white hover:border-foreground"
              } disabled:opacity-60`}
              data-testid={`admin-plan-switch-${id}`}
            >
              <div className="flex items-center justify-between gap-2 mb-1">
                <Icon className="w-4 h-4" strokeWidth={1.5} />
                {active && (
                  <span className="font-mono-data text-[10px] uppercase tracking-widest opacity-80">
                    current
                  </span>
                )}
                {loading && <Loader2 className="w-3.5 h-3.5 animate-spin" />}
              </div>
              <div className="font-semibold tracking-tight text-base">{label}</div>
              <div className={`text-xs mt-0.5 ${active ? "text-background/80" : "text-muted-foreground"}`}>
                {desc}
              </div>
              {!active && !loading && (
                <div className={`mt-3 inline-flex items-center gap-1 text-xs font-mono-data uppercase tracking-widest ${active ? "text-background" : "text-foreground/70"}`}>
                  switch <ArrowRight className="w-3 h-3" />
                </div>
              )}
            </button>
          );
        })}
      </div>

      <p className="text-xs text-muted-foreground mt-4">
        Tip: open <code className="font-mono-data text-[11px]">/app</code>, <code className="font-mono-data text-[11px]">/app/settings</code>, or <code className="font-mono-data text-[11px]">/pricing</code> in another tab — toggle here, refresh the other tab, and watch the UI change live.
      </p>
    </section>
  );
}
